import React, { createContext, useContext, useState, useEffect } from "react";
import api from "@/lib/api";
import { getAllUser } from "@/services/user";
import { getAllPropFirms } from "@/services/propFirmApi";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(
    () => localStorage.getItem("access_token") || null
  );
  const [loading, setLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [propFirms,setPropFirms] = useState()
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => {
    if (token) {
      localStorage.setItem("access_token", token);
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      localStorage.removeItem("access_token");
      delete api.defaults.headers.common["Authorization"];
    }
  }, [token]);

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.removeItem("user");
      }
    }
  }, []);

  const signin = async ({ email, password }) => {
  setLoading(true);
  try {
    const res = await api.post("/user/login", { email, password });
    const data = res.data;

    const accessToken = data.access_token;
    const refreshToken = data.refresh_token;
    const mfaToken = data.mfa_token; // challenge token when MFA required
    const tokenType = data.token_type || "Bearer";

    // Case 1: MFA required (no access token yet)
    if (!accessToken && mfaToken) {
      // store challenge only for verify step
      sessionStorage.setItem("mfa_challenge_token", mfaToken);
      return {
        needsMfaVerify: true,
        challenge_token: mfaToken,
        raw: data,
      };
    }

    // Case 2: MFA not set up yet (adjust if your API differs)
    if (!accessToken && !mfaToken) {
      return { needsMfaSetup: true, raw: data };
    }

    if (!accessToken) {
      throw new Error("No access_token in response");
    }

    // Case 3: full login (no MFA challenge)
    localStorage.setItem("access_token", accessToken);
    localStorage.setItem("refresh_token", refreshToken || "");
    localStorage.setItem("token_type", tokenType);
    setToken(accessToken);
    api.defaults.headers.common["Authorization"] = `${tokenType} ${accessToken}`;

    if (data.user) {
      setUser(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
    }

    return { needsMfaVerify: false, needsMfaSetup: false, raw: data };
  } finally {
    setLoading(false);
  }
};


const completeMfaLogin = (data) => {
  const accessToken = data.access_token;
  const refreshToken = data.refresh_token;
  const tokenType = data.token_type || "Bearer";

  if (!accessToken) {
    throw new Error("No access_token after MFA verify");
  }

  localStorage.setItem("access_token", accessToken);
  localStorage.setItem("refresh_token", refreshToken || "");
  localStorage.setItem("token_type", tokenType);
  localStorage.setItem("mfa_token", "enabled");

  setToken(accessToken);
  api.defaults.headers.common["Authorization"] = `${tokenType} ${accessToken}`;

  if (data.user) {
    setUser(data.user);
    localStorage.setItem("user", JSON.stringify(data.user));
  }

  sessionStorage.removeItem("mfa_challenge_token");
};

// put completeMfaLogin in value object
  const logout = () => {
    setToken(null);
    setUser(null);
    setUsers([]);
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("token_type");
    localStorage.removeItem("user");
    delete api.defaults.headers.common["Authorization"];
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const res = await getAllUser();
      const list = res?.data || (Array.isArray(res) ? res : []);
      setUsers(list);
      return list;
    } catch (error) {
      console.error(error);
      setUsers([]);
      return [];
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchPropFirms = async () => {
    try {
      setUsersLoading(true);
      const res = await getAllPropFirms();
      const list = res?.data || (Array.isArray(res) ? res : []);
      setPropFirms(list);
      return list;
    } catch (error) {
      console.error(error);
      setUsers([]);
      return [];
    } finally {
      setUsersLoading(false);
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    signin,
    logout,
    completeMfaLogin,
    setUser,
    setToken,
    users,
    propFirms,
    usersLoading,
    fetchUsers,
    fetchPropFirms,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;